import { cn } from "@/lib/utils";
import { teamMemberProfileHints } from "@/lib/teamMemberProfileHints";

export type PersonalizationHints = {
  lastGiftSummary?: string | null;
  inferredTags?: string[] | null;
  profileTags?: string[] | null;
  profileNotes?: string | null;
};

type PersonalizationHintsStripProps = PersonalizationHints & {
  className?: string;
  compact?: boolean;
};

/**
 * Surfaces known gift memory + profile enrichment when adjusting or approving.
 * Renders nothing when all fields are empty.
 */
export function PersonalizationHintsStrip({
  lastGiftSummary,
  inferredTags,
  profileTags,
  profileNotes,
  className,
  compact = false,
}: PersonalizationHintsStripProps) {
  const lastGift = lastGiftSummary?.trim() ?? "";
  const tags = (inferredTags ?? []).filter((t) => t?.trim());
  const giftProfileTags = (profileTags ?? []).filter((t) => t?.trim());
  const notes = profileNotes?.trim() ?? "";

  if (!lastGift && tags.length === 0 && giftProfileTags.length === 0 && !notes) {
    return null;
  }

  return (
    <div
      data-testid="personalization-hints"
      className={cn(
        "space-y-1 text-xs leading-snug text-charcoal/70",
        compact ? "mt-2" : "",
        className,
      )}
    >
      {lastGift ? (
        <p>
          <span className="font-medium text-charcoal/55">Last gift:</span>{" "}
          <span className="text-navy">{lastGift}</span>
        </p>
      ) : null}
      {giftProfileTags.length > 0 ? (
        <p>
          <span className="font-medium text-charcoal/55">Gift personality:</span>{" "}
          <span className="text-navy">{giftProfileTags.join(", ")}</span>
        </p>
      ) : null}
      {tags.length > 0 ? (
        <p>
          <span className="font-medium text-charcoal/55">We know they like:</span>{" "}
          <span className="text-navy">{tags.join(", ")}</span>
        </p>
      ) : null}
      {notes ? (
        <p>
          <span className="font-medium text-charcoal/55">Notes:</span>{" "}
          <span className="text-navy">{notes}</span>
        </p>
      ) : null}
    </div>
  );
}

/** Pick personalization fields from a team member row (or directory lookup). */
export function pickPersonalizationHints(
  members: Array<{
    id: string;
    last_gift_summary?: string | null;
    inferred_tags?: string[] | null;
    hobbies?: string[] | null;
    favourite_foods?: string[] | null;
    personal_notes?: string | null;
    gift_preferences?: unknown;
  }> | undefined,
  memberId: string | null | undefined,
): PersonalizationHints {
  if (!memberId || !members?.length) {
    return { lastGiftSummary: null, inferredTags: [] };
  }
  const member = members.find((m) => m.id === memberId);
  const profile = teamMemberProfileHints(member);
  const profileTags = [
    ...(profile.hobbies ?? []),
    ...(profile.favourite_foods ?? []),
    ...(profile.gift_preferences?.vibes ?? []),
  ];
  return {
    lastGiftSummary: member?.last_gift_summary ?? null,
    inferredTags: member?.inferred_tags ?? [],
    profileTags: profileTags.length > 0 ? profileTags : null,
    profileNotes: profile.personal_notes ?? profile.gift_preferences?.notes ?? null,
  };
}
